import React from 'react';
import { motion } from 'framer-motion';
import { Inbox, Sparkles, ShieldCheck, Send } from 'lucide-react';

const STEPS = [
  {
    n: '01',
    icon: Inbox,
    title: 'Connect your inbox',
    body: 'Core Trade plugs into Outlook, Gmail or your shared trade-confirms mailbox. Zero changes to how counterparties send confirmations.',
  },
  {
    n: '02',
    icon: Sparkles,
    title: 'AI extracts every field',
    body: 'Our domain-tuned models pull commodity, volume, price, laycan, port, incoterms, counterparty — across English, Arabic, recorded-call transcripts and free-form threads.',
  },
  {
    n: '03',
    icon: ShieldCheck,
    title: 'You verify in one screen',
    body: 'A clean, side-by-side ticket. Ambiguous fields are flagged for review. Every change is timestamped and attributed.',
  },
  {
    n: '04',
    icon: Send,
    title: 'Posted to TRM',
    body: 'One click books the trade in Allegro, Endur, RightAngle or your in-house system. Reconciliation sits in your dashboard, not your head.',
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="relative py-28 md:py-32 bg-canvas-dark">
      <div className="container-prose">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5"
          >
            How it works
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-display font-bold text-canvas-white text-balance"
          >
            From email thread to booked trade.
            <br className="hidden md:block" />{' '}
            <span className="text-ink-faint">Without a single re-typed field.</span>
          </motion.h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map(({ n, icon: Icon, title, body }, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative group"
            >
              <div className="relative h-full p-7 rounded-2xl border border-white/[0.07] bg-canvas-surface/50 hover:bg-canvas-surface/80 transition-all hover:border-white/[0.12] hover:-translate-y-0.5">
                <div className="flex items-center justify-between mb-7">
                  <span className="mono-tabular text-[11px] text-ink-faint">{n}</span>
                  <Icon size={18} className="text-amber" strokeWidth={1.6} />
                </div>
                <h3 className="text-[17px] font-semibold text-canvas-white tracking-tight mb-3">
                  {title}
                </h3>
                <p className="text-[13.5px] leading-relaxed text-ink-faint">
                  {body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
